<script setup>
/**
 * Modal "Reservar cliente" (Victor 2026-06-07). Captura el plan (con precio y
 * etiqueta del nombre auto del catálogo), la agenda de la sesión y el pago, y
 * registra quién lo agendó (el agente conectado) para métricas. Crea una
 * Reservation ligada al lead (deal). Independiente del sistema de facturación.
 */
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import ReservationPlansAPI from 'dashboard/api/reservationPlans';
import ReservationsAPI from 'dashboard/api/reservations';

const props = defineProps({
  dealId: { type: [Number, String], default: null },
  contactId: { type: [Number, String], default: null },
  conversationId: { type: [Number, String], default: null },
  contactName: { type: String, default: '' },
  phone: { type: String, default: '' },
  // Plan preseleccionado desde el modal "Planes" (Victor 2026-06-07).
  presetPlanId: { type: [Number, String], default: null },
});
const emit = defineEmits(['close', 'saved']);

const store = useStore();
const agentName = computed(() => {
  const u = store.getters.getCurrentUser || {};
  return u.available_name || u.name || 'Agente';
});

const PAYMENT_TYPES = ['Efectivo', 'Transferencia', 'Tarjeta'];

const plans = ref([]);
const saving = ref(false);
const error = ref(false);

const form = ref({
  reservation_plan_id: '',
  plan_name: '',
  plan_type: '',
  name_label: 'Nombre del cliente',
  client_name: '',
  location: '',
  session_on: '',
  session_time: '',
  total: '',
  paid: '',
  payment_type: 'Efectivo',
});
const sendReceipt = ref(true);

const pending = computed(() => {
  const t = Number(form.value.total) || 0;
  const p = Number(form.value.paid) || 0;
  return Math.max(0, t - p);
});

const fmt = n =>
  `RD$ ${(Number(n) || 0).toLocaleString('es-DO', { maximumFractionDigits: 0 })}`;

const onPlanChange = () => {
  const plan = plans.value.find(p => String(p.id) === String(form.value.reservation_plan_id));
  if (!plan) return;
  form.value.plan_name = plan.name;
  form.value.plan_type = plan.category;
  form.value.name_label = plan.name_label || 'Nombre del cliente';
  form.value.total = Number(plan.price) || 0;
};

const canSave = computed(
  () => form.value.reservation_plan_id && form.value.total !== '' && !saving.value
);

const save = async () => {
  if (!canSave.value) return;
  saving.value = true;
  error.value = false;
  try {
    await ReservationsAPI.create({
      deal_id: props.dealId,
      contact_id: props.contactId,
      conversation_id: props.conversationId,
      reservation_plan_id: form.value.reservation_plan_id,
      plan_name: form.value.plan_name,
      plan_type: form.value.plan_type,
      name_label: form.value.name_label,
      client_name: form.value.client_name,
      session_on: form.value.session_on || null,
      session_time: form.value.session_time,
      location: form.value.location,
      total: Number(form.value.total) || 0,
      paid: Number(form.value.paid) || 0,
      pending: pending.value,
      payment_type: form.value.payment_type,
      send_receipt: sendReceipt.value,
    });
    emit('saved');
    emit('close');
  } catch (e) {
    error.value = true;
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  try {
    const { data } = await ReservationPlansAPI.get();
    plans.value = data || [];
    // Plan preseleccionado desde el showroom: precarga plan + precio + etiqueta.
    if (props.presetPlanId) {
      form.value.reservation_plan_id = props.presetPlanId;
      onPlanChange();
    }
  } catch (e) {
    plans.value = [];
  }
});
</script>

<template>
  <teleport to="body">
    <div class="rc-overlay" @click.self="emit('close')">
      <div class="rc-modal">
        <!-- header -->
        <div class="rc-head">
          <span class="rc-ic">📅</span>
          <div class="rc-head-txt">
            <div class="rc-title">Reservar cliente</div>
            <div class="rc-sub">{{ contactName }}<span v-if="phone"> · {{ phone }}</span></div>
          </div>
          <button class="rc-x" @click="emit('close')">✕</button>
        </div>

        <div class="rc-body">
          <!-- Plan -->
          <div class="rc-grp">
            <div class="rc-gh">📦 Plan y lugar</div>
            <label class="rc-l">Plan seleccionado *</label>
            <select v-model="form.reservation_plan_id" class="rc-inp" @change="onPlanChange">
              <option value="" disabled>Elige un plan…</option>
              <option v-for="p in plans" :key="p.id" :value="p.id">
                {{ p.name }} — {{ fmt(p.price) }}
              </option>
            </select>
            <p v-if="!plans.length" class="rc-hint">
              No hay planes en el catálogo. Agrégalos en Ajustes → Catálogo de Planes.
            </p>
            <label class="rc-l rc-mt">Lugar de la sesión</label>
            <input v-model="form.location" class="rc-inp" type="text" placeholder="Ej. Jardín Botánico" />
          </div>

          <!-- Agenda -->
          <div class="rc-grp">
            <div class="rc-gh">🗓️ Agenda de la sesión</div>
            <div class="rc-row2">
              <div>
                <label class="rc-l">Fecha de la sesión</label>
                <input v-model="form.session_on" class="rc-inp" type="date" />
              </div>
              <div>
                <label class="rc-l">Hora de la sesión</label>
                <input v-model="form.session_time" class="rc-inp" type="time" />
              </div>
            </div>
            <label class="rc-l rc-mt">{{ form.name_label }}</label>
            <input v-model="form.client_name" class="rc-inp" type="text" placeholder="Nombre…" />
          </div>

          <!-- Pago -->
          <div class="rc-grp">
            <div class="rc-gh">💰 Pago</div>
            <div class="rc-row2">
              <div>
                <label class="rc-l">Costo total *</label>
                <input v-model="form.total" class="rc-inp" type="number" min="0" placeholder="0" />
              </div>
              <div>
                <label class="rc-l">Costo abonado</label>
                <input v-model="form.paid" class="rc-inp" type="number" min="0" placeholder="0" />
              </div>
            </div>
            <div class="rc-row2 rc-mt">
              <div>
                <label class="rc-l">Costo pendiente</label>
                <div class="rc-calc">{{ fmt(pending) }}</div>
              </div>
              <div>
                <label class="rc-l">Tipo de pago</label>
                <div class="rc-seg">
                  <button
                    v-for="t in PAYMENT_TYPES"
                    :key="t"
                    class="rc-o"
                    :class="{ on: form.payment_type === t }"
                    @click="form.payment_type = t"
                  >{{ t }}</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Registro automático -->
          <div class="rc-grp">
            <div class="rc-gh">🧾 Registro (automático)</div>
            <div class="rc-row2">
              <div>
                <label class="rc-l">Agendado por</label>
                <div class="rc-ro">{{ agentName }}</div>
              </div>
              <div>
                <label class="rc-l">Día de la reserva</label>
                <div class="rc-ro">Hoy</div>
              </div>
            </div>
            <p class="rc-metric">📊 Se guarda con tu usuario para las métricas de quién genera más reservas.</p>
          </div>

          <!-- Enviar recibo por WhatsApp -->
          <label class="rc-send" :class="{ on: sendReceipt }">
            <input v-model="sendReceipt" type="checkbox" />
            <span class="rc-send-box">{{ sendReceipt ? '✓' : '' }}</span>
            <span class="rc-send-txt">
              Enviar recibo al cliente por WhatsApp
              <span class="rc-send-sub">Se le manda el recibo de reservación al chat del cliente.</span>
            </span>
          </label>

          <p v-if="error" class="rc-err">No se pudo guardar, intenta de nuevo.</p>
        </div>

        <div class="rc-foot">
          <button class="rc-btn ghost" @click="emit('close')">Cancelar</button>
          <button class="rc-btn gold" :disabled="!canSave" @click="save">
            📅 Crear reserva
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style lang="scss" scoped>
.rc-overlay { position: fixed; inset: 0; z-index: 10000; background: rgba(0, 0, 0, 0.6); display: grid; place-items: center; padding: 20px; }
.rc-modal { width: 560px; max-width: 100%; max-height: 92vh; display: flex; flex-direction: column; background: linear-gradient(180deg, #141416, #0d0d0f); border: 1px solid rgba(201, 163, 90, 0.5); border-radius: 16px; overflow: hidden; box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6); }
.rc-head { display: flex; align-items: center; gap: 10px; padding: 16px 20px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.rc-ic { width: 34px; height: 34px; border-radius: 10px; background: rgba(201, 163, 90, 0.12); border: 1px solid rgba(201, 163, 90, 0.5); display: grid; place-items: center; font-size: 16px; }
.rc-title { font-size: 16px; font-weight: 800; color: #fff; }
.rc-sub { font-size: 11px; color: #8b8b91; margin-top: 1px; }
.rc-x { margin-left: auto; width: 30px; height: 30px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.08); background: none; color: #8b8b91; cursor: pointer; }
.rc-body { padding: 16px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; }
.rc-grp { display: flex; flex-direction: column; }
.rc-gh { font-size: 9.5px; letter-spacing: 1.2px; text-transform: uppercase; color: #d8bd86; font-weight: 800; margin-bottom: 9px; }
.rc-l { font-size: 11px; color: #8b8b91; font-weight: 600; margin-bottom: 5px; }
.rc-mt { margin-top: 11px; }
.rc-inp { background: #0e0e10; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 9px; padding: 9px 11px; font-size: 13px; color: #fff; outline: none; width: 100%; }
.rc-inp:focus { border-color: rgba(201, 163, 90, 0.6); }
.rc-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; }
.rc-calc { background: rgba(204, 107, 107, 0.08); border: 1px solid rgba(204, 107, 107, 0.35); border-radius: 9px; padding: 9px 11px; font-size: 14px; color: #cc6b6b; font-weight: 700; }
.rc-ro { background: #101012; border: 1px dashed rgba(255, 255, 255, 0.12); border-radius: 9px; padding: 9px 11px; font-size: 13px; color: #d8bd86; font-weight: 700; }
.rc-seg { display: flex; gap: 5px; }
.rc-o { flex: 1; font-size: 11px; font-weight: 700; padding: 9px 4px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.08); color: #8b8b91; background: none; cursor: pointer; }
.rc-o.on { color: #5fae7e; background: rgba(95, 174, 126, 0.14); border-color: rgba(95, 174, 126, 0.4); }
.rc-hint { font-size: 10.5px; color: #cc6b6b; margin-top: 6px; }
.rc-metric { font-size: 10.5px; color: #8b8b91; margin-top: 9px; line-height: 1.5; }
.rc-send { display: flex; align-items: flex-start; gap: 9px; padding: 11px 13px; border-radius: 10px; border: 1px solid rgba(95, 174, 126, 0.4); background: rgba(95, 174, 126, 0.1); cursor: pointer; }
.rc-send input { display: none; }
.rc-send-box { width: 18px; height: 18px; border-radius: 5px; border: 1px solid rgba(95, 174, 126, 0.5); display: grid; place-items: center; flex: none; font-size: 12px; font-weight: 900; color: #06210f; }
.rc-send.on .rc-send-box { background: #5fae7e; }
.rc-send-txt { font-size: 12.5px; font-weight: 600; color: #fff; }
.rc-send-sub { display: block; font-size: 10.5px; color: #8b8b91; font-weight: 400; margin-top: 1px; }
.rc-err { font-size: 12px; color: #cc6b6b; }
.rc-foot { padding: 14px 20px; display: flex; gap: 10px; border-top: 1px solid rgba(255, 255, 255, 0.08); }
.rc-btn { flex: 1; text-align: center; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 12px; cursor: pointer; border: none; }
.rc-btn.gold { background: #c9a35a; color: #1a1206; flex: 2; }
.rc-btn.gold:disabled { opacity: 0.4; cursor: default; }
.rc-btn.ghost { background: none; border: 1px solid rgba(255, 255, 255, 0.1); color: #8b8b91; }
</style>
